/**
 * Utility for encoding and decoding user information in feedback cycle URLs
 */

interface EncodedCycleInfo {
  cycleId?: string;
  userId?: string;
  participantId?: string;
  token?: string;
  timestamp?: number;
}

/**
 * Convert base64 to base64url (URL-safe)
 */
function base64ToBase64url(base64: string): string {
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Encode cycle and user information into a URL-safe string
 */
export function encodeCycleUrl(cycleId: string, userId: string, participantId?: string): string {
  const info: EncodedCycleInfo = {
    cycleId,
    userId,
    participantId,
    timestamp: Date.now()
  };
  
  const jsonString = JSON.stringify(info);
  
  // Use different encoding method based on environment
  let base64: string;
  if (typeof Buffer !== 'undefined') {
    // Node.js environment
    base64 = Buffer.from(jsonString).toString('base64');
  } else {
    // Browser environment
    base64 = btoa(jsonString);
  }
  
  // Convert to URL-safe base64url
  return base64ToBase64url(base64);
}

/**
 * Convert base64url string to base64 string
 */
function base64urlToBase64(str: string): string {
  // Replace URL-safe characters with base64 characters
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  
  // Add padding if needed
  while (base64.length % 4) {
    base64 += '=';
  }
  
  return base64;
}

/**
 * Decode user information from encoded cycle URL parameter
 */
export function decodeCycleUrl(encoded: string): EncodedCycleInfo | null {
  try {
    // Convert base64url to base64 for browser compatibility
    const base64 = base64urlToBase64(encoded);
    
    // Use different decoding method based on environment
    let decoded: string;
    if (typeof Buffer !== 'undefined') {
      // Node.js environment
      decoded = Buffer.from(base64, 'base64').toString();
    } else {
      // Browser environment
      decoded = atob(base64);
    }
    
    const info = JSON.parse(decoded) as EncodedCycleInfo;
    
    // Handle new token-based payloads
    if (info && typeof info === 'object') {
      if (info.token) {
        return {
          token: info.token,
          cycleId: info.cycleId,
          participantId: info.participantId,
        };
      }

      // Legacy encoding with userId + cycleId
      if (info.cycleId && info.userId) {
        return info;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Failed to decode cycle URL:', error);
    return null;
  }
}

/**
 * Generate a user-specific feedback URL with encoded information
 */
export function generateUserFeedbackUrl(cycleId: string, userId: string, participantId?: string): string {
  const encoded = encodeCycleUrl(cycleId, userId, participantId);
  return `${process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000'}/feedback/cycle/${encoded}`;
}

/**
 * Check if a string looks like an encoded cycle URL vs standard UUID
 * NOTE: All URLs should now be encoded, but keeping this for backward compatibility during transition
 */
export function isEncodedCycleUrl(param: string): boolean {
  // All feedback URLs should now be encoded - try to decode first
  try {
    const decoded = decodeCycleUrl(param);
    return decoded !== null;
  } catch {
    // Fallback: UUID pattern check for backward compatibility
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return !uuidPattern.test(param);
  }
}

/**
 * Create encoded URL for existing cycle and user
 */
export async function createEncodedUrlForUser(cycleId: string, userEmail: string): Promise<string | null> {
  // This would typically query the database to get user ID and participant ID
  // For now, return a placeholder implementation
  console.log(`Would create encoded URL for cycle ${cycleId} and user ${userEmail}`);
  return null;
}
